import { useEffect, useState } from 'react';
import { Link } from 'react-router-dom';
import { toast } from 'react-toastify';
import Swal from 'sweetalert2';
import withReactContent from 'sweetalert2-react-content';
import { swalConfig, toastConfig } from '@/config';
import useRouteStore from '@/hooks/useRouteStore';
import { getCampaigns, deleteCampaign } from '@/api';

const statusBadge = {
  draft:      { label: 'Borrador',    className: 'bg-gray-100 text-gray-600' },
  queued:     { label: 'En cola',     className: 'bg-blue-100 text-blue-700' },
  processing: { label: 'Procesando',  className: 'bg-yellow-100 text-yellow-700' },
  sent:       { label: 'Enviada',     className: 'bg-green-100 text-green-700' },
};

const StatChip = ({ icon, value, label }) => (
  <div className='flex items-center gap-1 text-xs text-gray-500'>
    {icon}
    <span className='font-semibold text-gray-700'>{value}</span>
    <span>{label}</span>
  </div>
);

const IconSent = () => (
  <svg className='w-3.5 h-3.5 text-gray-400' fill='none' viewBox='0 0 24 24' stroke='currentColor' strokeWidth={2}>
    <path strokeLinecap='round' strokeLinejoin='round' d='M12 19l9 2-9-18-9 18 9-2zm0 0v-8' />
  </svg>
);
const IconOpen = () => (
  <svg className='w-3.5 h-3.5 text-gray-400' fill='none' viewBox='0 0 24 24' stroke='currentColor' strokeWidth={2}>
    <path strokeLinecap='round' strokeLinejoin='round' d='M3 19V9.5L12 4l9 5.5V19a1 1 0 01-1 1H4a1 1 0 01-1-1z' />
    <path strokeLinecap='round' strokeLinejoin='round' d='M9 19v-6h6v6' />
  </svg>
);
const IconBounce = () => (
  <svg className='w-3.5 h-3.5 text-gray-400' fill='none' viewBox='0 0 24 24' stroke='currentColor' strokeWidth={2}>
    <path strokeLinecap='round' strokeLinejoin='round' d='M3 10h10a8 8 0 018 8v2M3 10l6 6m-6-6l6-6' />
  </svg>
);

const Campaigns = () => {
  const mySwal = withReactContent(Swal);
  const setRoute = useRouteStore((state) => state.setRoute);

  const [campaigns, setCampaigns] = useState([]);
  const [loading, setLoading]     = useState(true);

  useEffect(() => {
    setRoute([
      { name: 'Inicio',    url: '/administracion' },
      { name: 'Correos',   url: '/administracion/correos' },
      { name: 'Campañas',  url: '/administracion/correos/campanas' },
    ]);
    fetchCampaigns();
  }, []);

  const fetchCampaigns = () => {
    setLoading(true);
    getCampaigns().then((res) => {
      setCampaigns(res.data.result ?? res.data ?? []);
    }).finally(() => setLoading(false));
  };

  const handleDelete = async (campaign) => {
    const result = await mySwal.fire({
      ...swalConfig.delete,
      text: `¿Eliminar la campaña "${campaign.name}"?`,
    });
    if (!result.isConfirmed) return;
    try {
      await deleteCampaign(campaign._id);
      toast.success('Campaña eliminada', toastConfig);
      setCampaigns((prev) => prev.filter((c) => c._id !== campaign._id));
    } catch (err) {
      toast.error(err.response?.data?.message || 'Error al eliminar la campaña', toastConfig);
    }
  };

  return (
    <div className='font-montserrat w-full'>

      <div className='flex items-center justify-between pb-1 mb-5 border-b-2'>
        <h1 className='font-semibold text-xl'>Campañas</h1>
        <Link
          to='/administracion/correos/campanas/nueva'
          className='flex items-center gap-1.5 bg-red-1 hover:bg-red-2 text-white text-sm font-semibold px-3 py-1.5 rounded-lg transition-colors'
        >
          <svg className='w-4 h-4' fill='none' viewBox='0 0 24 24' stroke='currentColor' strokeWidth={2.5}>
            <path strokeLinecap='round' strokeLinejoin='round' d='M12 4v16m8-8H4' />
          </svg>
          Nueva campaña
        </Link>
      </div>

      {loading ? (
        <div className='flex flex-col gap-2'>
          {[...Array(4)].map((_, i) => (
            <div key={i} className='h-16 w-full rounded-lg animate-pulse bg-gray-100' />
          ))}
        </div>
      ) : campaigns.length === 0 ? (
        <p className='text-sm text-gray-400 italic text-center py-12'>No hay campañas todavía.</p>
      ) : (
        <div className='flex flex-col gap-2'>
          {campaigns.map((campaign) => {
            const { label, className } = statusBadge[campaign.status] ?? statusBadge.draft;
            const stats = campaign.stats ?? {};
            const isSent = campaign.status !== 'draft';

            return (
              <div
                key={campaign._id}
                className='flex items-center gap-4 bg-white border border-gray-100 rounded-lg px-4 py-3 shadow-sm'
              >
                {/* Name + list */}
                <div className='flex-1 min-w-0'>
                  <p className='font-semibold text-gray-800 truncate'>{campaign.name}</p>
                  <p className='text-xs text-gray-400 mt-0.5 truncate'>
                    {campaign.contact_list_id?.name
                      ? `Lista: ${campaign.contact_list_id.name}`
                      : 'Sin lista de contacto'}
                  </p>
                </div>

                {/* Stats */}
                <div className='hidden md:flex items-center gap-4 shrink-0'>
                  <StatChip icon={<IconSent />}   value={stats.sent   ?? 0} label='enviados' />
                  <StatChip icon={<IconOpen />}   value={stats.open   ?? 0} label='abiertos' />
                  <StatChip icon={<IconBounce />} value={stats.bounce ?? 0} label='rebotes'  />
                </div>

                {/* Status badge */}
                <span className={`shrink-0 inline-block px-2.5 py-0.5 rounded-full text-xs font-semibold ${className}`}>
                  {label}
                </span>

                {/* Actions */}
                <div className='flex items-center gap-2 shrink-0'>
                  {!isSent && (
                    <Link
                      to={`/administracion/correos/campanas/${campaign._id}`}
                      className='flex items-center gap-1 text-xs font-semibold text-red-1 border border-red-1 px-3 py-1.5 rounded-lg hover:bg-red-1 hover:text-white transition-colors'
                    >
                      <svg className='w-3.5 h-3.5' fill='none' viewBox='0 0 24 24' stroke='currentColor' strokeWidth={2}>
                        <path strokeLinecap='round' strokeLinejoin='round' d='M11 5H6a2 2 0 00-2 2v11a2 2 0 002 2h11a2 2 0 002-2v-5m-1.414-9.414a2 2 0 112.828 2.828L11.828 15H9v-2.828l8.586-8.586z' />
                      </svg>
                      Editar
                    </Link>
                  )}
                  {!isSent && (
                    <button
                      onClick={() => handleDelete(campaign)}
                      className='flex items-center gap-1 text-xs font-semibold text-gray-400 border border-gray-200 px-3 py-1.5 rounded-lg hover:border-red-1 hover:text-red-1 transition-colors'
                    >
                      <svg className='w-3.5 h-3.5' fill='none' viewBox='0 0 24 24' stroke='currentColor' strokeWidth={2}>
                        <path strokeLinecap='round' strokeLinejoin='round' d='M19 7l-.867 12.142A2 2 0 0116.138 21H7.862a2 2 0 01-1.995-1.858L5 7m5 4v6m4-6v6m1-10V4a1 1 0 00-1-1h-4a1 1 0 00-1 1v3M4 7h16' />
                      </svg>
                      Eliminar
                    </button>
                  )}
                  {isSent && (
                    <span className='text-xs text-gray-400 italic'>
                      {campaign.sentAt ? new Date(campaign.sentAt).toLocaleDateString() : ''}
                    </span>
                  )}
                </div>
              </div>
            );
          })}
        </div>
      )}
    </div>
  );
};

export default Campaigns;
